import { getPropositions } from '@rei/adobe-target-functions';
import pathConfiguration from '../pages/deliveryIssues/pathConfiguration';
import VALIDATION_PATTERNS from './constants';

let timeOutID = null;

const maxTimeToWaitForAdobeTarget = () => new Promise((_, reject) => {
  timeOutID = setTimeout(() => reject(new Error('Adobe Target is taking more than 3 seconds to respond')), 3000);
});

const getExperienceData = (adobeValue) => getPropositions(adobeValue)
  .then((propositions) => {
    const filteredPropositions = propositions.filter((item) => item && item.format === 'application/json');
    if (filteredPropositions.length > 0) {
      return filteredPropositions[0].content;
    }
    return null;
  })
  .catch(() => null);

const getAdobeTargetJson = async (adobeValue) => {
  try {
    const adobeTargetValue = await Promise.race(
      [
        getExperienceData(adobeValue),
        maxTimeToWaitForAdobeTarget(),
      ],
    );
    clearTimeout(timeOutID);
    timeOutID = null;
    if (adobeTargetValue?.length) {
      const { experience } = adobeTargetValue[0];
      return experience;
    }
    return null;
  } catch (error) {
    return null;
  }
};

const getStorageValues = (itemValue) => { // this is potentially deprecated, since useAppStorage can handle all the storage operations
  try {
    if (window && typeof window !== 'undefined') {
      return sessionStorage.getItem(itemValue);
    }
    return null;
  } catch (e) {
    return null;
  }
};

const scrollToTop = () => { // this is potentially deprecated, since this function is defined in helpers.ts file
  try {
    if (window && typeof window !== 'undefined') {
      setTimeout(() => { window.scrollTo(0, 0); }, 500);
    }
  } catch (e) { /* empty */ }
};

const camelCase = (str) => str
  .replace(/^[^A-Z0-9]+/gi, '')
  .toLowerCase()
  .replace(/([^A-Z0-9]+)(.)/ig, (match) => match.replace(/^[^A-Z0-9]+/gi, '').toUpperCase());

const camelCaseWithoutSC = (str) => str
  .replace(/^[^A-Z0-9]+/gi, '')
  .toLowerCase()
  .replace(/([^A-Z0-9]+)(.)/ig, (match) => match.replace(/^[^A-Z0-9]+/gi, '').toUpperCase())
  .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
  .join('');

const snakeCase = (str) => str
  .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
  .map((x) => x[0].toUpperCase() + x.substring(1))
  .join('_');

const checkLandingURL = () => {
  try {
    if (window && typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      return url.pathname;
    }
    return '';
  } catch (e) {
    return '';
  }
};

const getPathConfiguration = (namePath) => {
  const arrayPathConfiguration = pathConfiguration.pathConfiguration;
  const configuration = arrayPathConfiguration.find((step) => step.namePath === namePath);
  return JSON.parse(JSON.stringify(configuration));
};

const isValidRequiredField = (value) => { // potentially deprecated as this can be handled by useValidationFields composable functions
  if (!value || value.trim() === '') {
    return 'This field is required';
  }
  return '';
};
const isValidEmail = (email) => { // potentially deprecated as this can be handled by useValidationFields composable functions
  const fieldRequired = isValidRequiredField(email);
  if (!email || email.trim() === '') {
    return fieldRequired;
  }
  if (!VALIDATION_PATTERNS.EMAIL_REGEXP.test(email)) {
    return 'Please enter a valid email address';
  }
  return '';
};
const isValidOrderNumber = (orderNumber) => { // potentially deprecated as this can be handled by useValidationFields composable functions
  const fieldRequired = isValidRequiredField(orderNumber);
  if (fieldRequired !== '') {
    return fieldRequired;
  }
  if (!VALIDATION_PATTERNS.SAFE_TEXT_REGEXP.test(orderNumber)
    || !VALIDATION_PATTERNS.ORDER_NUMBER_REGEXP.test(orderNumber)
    || orderNumber.length < 10) {
    return 'Please enter valid order number';
  }
  return '';
};
const isValidDescriptionField = (value, maxLength) => { // potentially deprecated as this can be handled by useValidationFields composable functions
  const fieldRequired = isValidRequiredField(value);
  if (fieldRequired !== '') {
    return fieldRequired;
  }
  if (value.length > maxLength) {
    return `The max character count is ${maxLength}`;
  }
  if (!VALIDATION_PATTERNS.FULL_SAFE_TEXT_REGEXP.test(value) || VALIDATION_PATTERNS.URLS.test(value)) {
    return 'Invalid input! Please remove any special character from your message';
  }
  if (!VALIDATION_PATTERNS.FULL_SAFE_TEXT_REGEXP.test(value) || VALIDATION_PATTERNS.URLS.test(value)) {
    return 'Invalid input! Please remove any special character from your message';
  }
  if (!VALIDATION_PATTERNS.FULL_SAFE_TEXT_REGEXP.test(value) || VALIDATION_PATTERNS.URLS.test(value)) {
    return 'Invalid input! Please remove any special character from your message';
  }
  return '';
};

const replaceCharsFunction = (value) => {
  const replaceChars = {
    '&': 'and',
    ' ': '-',
    '/': '',
  };
  return value.replace(/[& /]/g, (m) => replaceChars[m]);
};

// params: hc | a | ctgry
function isPageOfType(...rest) {
  const fields = [...rest];
  const url = window.location.href;
  return fields.some((val) => url.includes(`?${val}=`));
}

export {
  getPathConfiguration,
  isValidEmail,
  isValidRequiredField,
  isValidOrderNumber,
  isValidDescriptionField,
  checkLandingURL,
  getStorageValues,
  camelCase,
  snakeCase,
  camelCaseWithoutSC,
  scrollToTop,
  getAdobeTargetJson,
  replaceCharsFunction,
  isPageOfType,
};
